/**
 * File navigation.js.
 *
 * Handles toggling the navigation menu for small screens and enables TAB key
 * navigation support for dropdown menus.
 */
(() => {
  const initNavigation = () => {
    const siteHeader = document.getElementById('masthead');
    const siteNavigation = siteHeader.querySelector('#site-navigation');
    const toggleButton = siteHeader.querySelector('#site-navigation-toggle');
    const meganav = siteHeader.querySelector('#meganav');
    const linkWrappers = siteHeader.querySelectorAll('.site-navigation__links-item');
    const dropdowns = siteHeader.querySelectorAll('.site-navigation__links-dropdown');
    const requiredElements = [siteNavigation, toggleButton, meganav, linkWrappers, dropdowns];

    if (requiredElements.some((element) => element === null)) {
      console.error('Navigation initiation error, some element(s) not found.');
      return;
    }

    const breakpoint = 1024;

    const deviceDetails = {
      isTouch: () => 'ontouchstart' in window || navigator.maxTouchPoints,
      isMobile: () => window.innerWidth < breakpoint,
    };

    const { isTouch, isMobile } = deviceDetails;

    const throttleResize = (callback, delay) => {
      let resizing = false;

      window.addEventListener('resize', () => {
        if (!resizing) {
          resizing = true;

          setTimeout(() => {
            callback();
            resizing = false;
          }, delay);
        }
      });
    };

    const navAnimation = gsap.from(siteHeader, {
      yPercent: -100,
      paused: true,
      ease: 'power2.in',
      duration: 0.2,
    }).progress(1);

    ScrollTrigger.create({
      trigger: siteHeader,
      start: 'top top',
      end: 9999,
      scrub: true,
      onUpdate: (self) => {
        if (self.direction === -1) {
          navAnimation.play();
        } else {
          navAnimation.reverse();
        }
      },
    });

    function resetBoundingBoxModifiers(element) {
      element.style.top = '';
      element.style.left = '';
    }

    function ensureBoundingBoxContainment(element) {
      if (!element || isMobile() || isTouch()) return;

      resetBoundingBoxModifiers(element);

      const rect = element.getBoundingClientRect();

      if (rect.top < 0) element.style.top = `${Math.abs(rect.top)}px`;
      // if (rect.left < 0) element.style.left = `${Math.abs(rect.left)}px`;
      if (rect.left < 0) {
        element.style.left = `${0}px`;
        element.style.right = 'auto';
      }
    }

    function adjustOpenContextMenus(element = null) {
      if (element !== null) {
        ensureBoundingBoxContainment(element);
        return;
      }

      const expandedMenus = document.querySelectorAll('.site-navigation__links-dropdown.is-expanded');

      Array.from(expandedMenus).forEach((menu) => {
        ensureBoundingBoxContainment(menu);
      });
    }
    throttleResize(adjustOpenContextMenus, 250);

    function closeAllDropdowns() {
      dropdowns.forEach((dropdown) => {
        const dropdownToggle = dropdown.previousElementSibling;
        const dropdownMenu = dropdown.firstElementChild;
        const parentLink = dropdownToggle.previousElementSibling;
        if (!dropdownToggle || !dropdownMenu || !parentLink) return;
        dropdownToggle.setAttribute('aria-expanded', false);
        dropdown.setAttribute('aria-hidden', true);
        dropdown.setAttribute('aria-expanded', false);
        dropdown.classList.remove('is-expanded');
        dropdownMenu.classList.remove('is-expanded');
        parentLink.classList.remove('active');
      });
    }

    function openDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink) {
      dropdownToggle.setAttribute('aria-expanded', true);
      dropdown.setAttribute('aria-expanded', true);
      dropdown.setAttribute('aria-hidden', false);
      dropdown.classList.add('is-expanded');
      dropdownMenu.classList.add('is-expanded');
      parentLink.classList.add('active');

      adjustOpenContextMenus(dropdown);
    }

    function closeDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink) {
      dropdownToggle.setAttribute('aria-expanded', false);
      dropdown.setAttribute('aria-expanded', false);
      dropdown.setAttribute('aria-hidden', true);
      dropdown.classList.remove('is-expanded');
      dropdownMenu.classList.remove('is-expanded');
      parentLink.classList.remove('active');
    }

    function toggleDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink) {
      const isExpanded = dropdownToggle.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        closeDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink);
      } else {
        openDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink);
      }
    }

    function mouseLeaveEvent(event, linkWrapper, dropdownToggle, dropdown, dropdownMenu, parentLink) {
      const isMouseOverDropdown = dropdown.contains(event.relatedTarget);
      // console.log('mouseleave', isMouseOverDropdown, event.relatedTarget);

      const delay = 150;
      setTimeout(() => {
        if (!isMouseOverDropdown) {
          closeDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink);
        }
      }, delay);
    }

    function mouseEnterEvent(linkWrapper, dropdownToggle, dropdown, dropdownMenu, parentLink) {
      // console.log('mouseenter');
      closeAllDropdowns();
      toggleDropdown(dropdownToggle, dropdown, dropdownMenu, parentLink);
    }

    function mouseEventsHandler(linkWrapper, dropdownToggle, dropdown, dropdownMenu, parentLink) {
      linkWrapper.addEventListener('mouseleave', (event) => {
        mouseLeaveEvent(event, linkWrapper, dropdownToggle, dropdown, dropdownMenu, parentLink);
      });
      linkWrapper.addEventListener('mouseenter', () => {
        mouseEnterEvent(linkWrapper, dropdownToggle, dropdown, dropdownMenu, parentLink);
      });
    }

    function bindDropdowns() {
      linkWrappers.forEach((linkWrapper) => {
        const link = linkWrapper.querySelector('.site-navigation__links-link');
        const dropdownToggle = linkWrapper.querySelector('.site-navigation__links-button');
        const dropdown = linkWrapper.querySelector('.site-navigation__links-dropdown');

        if (!link || !dropdownToggle || !dropdown) return;

        const dropdownMenu = dropdown.firstElementChild;

        if (!isTouch() && !isMobile()) {
          mouseEventsHandler(linkWrapper, dropdownToggle, dropdown, dropdownMenu, link);
        }

        const dropdownToggleClickHandler = () => {
          toggleDropdown(dropdownToggle, dropdown, dropdownMenu, link);
        };

        dropdownToggle.addEventListener('click', dropdownToggleClickHandler);
      });
    }
    bindDropdowns();

    const openNavMenu = () => {
      siteHeader.classList.add('mobile-menu-open');
      meganav.classList.add('mobile-menu-open');
      meganav.firstElementChild.classList.add('mobile-menu-open');
      meganav.setAttribute('aria-expanded', true);
      toggleButton.setAttribute('aria-expanded', true);
      document.body.classList.add('no-scroll');
    };

    const closeNavMenu = () => {
      siteHeader.classList.remove('mobile-menu-open');
      meganav.classList.remove('mobile-menu-open');
      meganav.firstElementChild.classList.remove('mobile-menu-open');
      meganav.setAttribute('aria-expanded', false);
      toggleButton.setAttribute('aria-expanded', false);
      document.body.classList.remove('no-scroll');
      closeAllDropdowns();
    };

    const toggleNavMenu = () => {
      const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        closeNavMenu();
      } else {
        openNavMenu();
      }
    };

    toggleButton.addEventListener('click', toggleNavMenu);
  };

  if (document.readyState === 'complete' || (document.readyState !== 'loading' && !document.documentElement.doScroll)) {
    initNavigation();
  } else {
    document.addEventListener('DOMContentLoaded', initNavigation);
  }
})();
